import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CoinPackageSummary,
  ComplianceStatusResponse,
  ShowSummary,
  UserSpendLimits,
} from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import {
  CPM_HOLD_MS,
  estimateCpmRemainingMs,
  formatCoins,
  formatCpmCountdown,
  formatUsdCents,
  holdRemainingMs,
  resolveCpmRunwayPhase,
  type CpmRunwayPhase,
} from '@fetmode/utils';
import { Button } from '@fetmode/ui';

import './CpmRunwayOverlay.css';

export interface CpmRunwayOverlayProps {
  show: ShowSummary;
  userId: string | null;
  coinBalance: number;
  compliance: ComplianceStatusResponse | null;
  shopOpen: boolean;
  onShopOpenChange: (open: boolean) => void;
  onBalanceChange: (total: number) => void;
}

function holdUntilForUser(show: ShowSummary, userId: string | null): string | null {
  if (show.viewerBilling?.holdUntil) {
    return show.viewerBilling.holdUntil;
  }
  if (!userId) {
    return null;
  }
  return show.billingHolds?.find((hold) => hold.userId === userId)?.holdUntil ?? null;
}

export function CpmRunwayOverlay({
  show,
  userId,
  coinBalance,
  compliance,
  shopOpen,
  onShopOpenChange,
  onBalanceChange,
}: CpmRunwayOverlayProps): ReactElement | null {
  const { api } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  const [dismissedWarnForShow, setDismissedWarnForShow] = useState<string | null>(null);
  const [packages, setPackages] = useState<CoinPackageSummary[]>([]);
  const [limits, setLimits] = useState<UserSpendLimits | null>(null);
  const [isBuying, setIsBuying] = useState(false);
  const [buyError, setBuyError] = useState<string | null>(null);
  const [localHoldUntil, setLocalHoldUntil] = useState<string | null>(null);
  const assumedBilledAtRef = useRef<string | null>(null);

  const billing = show.viewerBilling ?? null;
  const serverHoldUntil = holdUntilForUser(show, userId);
  if (!billing) {
    assumedBilledAtRef.current = null;
  } else if (!billing.lastBilledAt && !assumedBilledAtRef.current) {
    assumedBilledAtRef.current = new Date().toISOString();
  }
  const lastBilledAt = billing?.lastBilledAt ?? assumedBilledAtRef.current;
  const remainingMs = billing
    ? estimateCpmRemainingMs({
        balanceCoins: coinBalance,
        coinsPerMinute: billing.coinsPerMinute,
        lastBilledAt,
        now,
      })
    : Number.POSITIVE_INFINITY;

  useEffect(() => {
    if (!billing) {
      setLocalHoldUntil(null);
      return;
    }
    if (serverHoldUntil) {
      setLocalHoldUntil(null);
      return;
    }
    if (remainingMs <= 0) {
      setLocalHoldUntil((current) => current ?? new Date(Date.now() + CPM_HOLD_MS).toISOString());
      return;
    }
    setLocalHoldUntil(null);
  }, [billing, remainingMs, serverHoldUntil]);

  const holdUntil = serverHoldUntil ?? localHoldUntil;
  const holdMs = holdRemainingMs(holdUntil, now);
  const phase: CpmRunwayPhase = billing
    ? resolveCpmRunwayPhase({ remainingMs, holdUntil, now })
    : 'ok';
  const clockMs = phase === 'hold' ? (holdMs > 0 ? holdMs : remainingMs) : remainingMs;
  const showWarnCard = phase === 'warn' && dismissedWarnForShow !== show.id;
  const forceShop = phase === 'countdown' || phase === 'hold';
  const cardOpen = shopOpen || forceShop || showWarnCard;

  useEffect(() => {
    if (!billing && !shopOpen) {
      return;
    }
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 250);
    return () => {
      window.clearInterval(timer);
    };
  }, [billing, shopOpen]);

  useEffect(() => {
    if (phase !== 'warn') {
      setDismissedWarnForShow((current) => (current === show.id ? null : current));
    }
  }, [phase, show.id]);

  useEffect(() => {
    if (!cardOpen) {
      return;
    }
    let cancelled = false;
    void Promise.all([api.listCoinPackages(), api.getSpendLimits()])
      .then(([pkgResult, spendLimits]) => {
        if (!cancelled) {
          setPackages(pkgResult.packages.filter((pkg) => pkg.enabled));
          setLimits(spendLimits.limits);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBuyError(t('common.show.cpmBuyFailed'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, cardOpen]);

  useEffect(() => {
    if (!forceShop) {
      return;
    }
    const refreshBalance = (): void => {
      void api
        .getSpendWallet()
        .then((wallet) => {
          onBalanceChange(wallet.total);
        })
        .catch(() => undefined);
    };
    refreshBalance();
    const timer = window.setInterval(refreshBalance, 4_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [api, forceShop, onBalanceChange]);

  const buyCoins = useCallback(
    async (input: { packageId?: string; coins?: number }): Promise<boolean> => {
      setIsBuying(true);
      setBuyError(null);
      try {
        const status = await api.getComplianceStatus();
        if (!status.canAccess) {
          setBuyError(t('common.show.cpmBuyVerify'));
          return false;
        }
        const created = await api.createCoinPurchase(input);
        const completed = await api.confirmCoinPurchase(created.purchaseId);
        onBalanceChange(completed.wallet.total);
        onShopOpenChange(false);
        return true;
      } catch (err: unknown) {
        setBuyError(err instanceof ApiError ? err.message : t('common.show.cpmBuyFailed'));
        return false;
      } finally {
        setIsBuying(false);
      }
    },
    [api, onBalanceChange, onShopOpenChange],
  );

  const handleAutoReload = useCallback(async (): Promise<void> => {
    const amount = limits?.autoTopUpAmountCoins ?? 500;
    try {
      await api.updateSpendLimits({
        autoTopUpEnabled: true,
        autoTopUpAmountCoins: amount,
        autoTopUpTriggerCoins: limits?.autoTopUpTriggerCoins ?? 50,
      });
    } catch {
      // Purchase can still proceed; the toggle is best-effort.
    }
    await buyCoins({ coins: amount });
  }, [api, buyCoins, limits]);

  if (!cardOpen) {
    if (phase === 'warn' && dismissedWarnForShow === show.id) {
      return (
        <div className="cpm-runway" role="status">
          <button
            type="button"
            className="cpm-runway__chip"
            onClick={() => {
              onShopOpenChange(true);
            }}
          >
            {t('common.show.cpmRemainingChip').replace('{{time}}', formatCpmCountdown(clockMs))}
          </button>
        </div>
      );
    }
    return null;
  }

  const title =
    phase === 'hold'
      ? t('common.show.cpmHoldTitle')
      : phase === 'countdown'
        ? t('common.show.cpmCountdownTitle')
        : phase === 'warn'
          ? t('common.show.cpmWarnTitle')
          : t('common.show.cpmShopTitle');
  const body =
    phase === 'hold'
      ? t('common.show.cpmHoldBody')
      : phase === 'countdown'
        ? t('common.show.cpmCountdownBody')
        : phase === 'warn'
          ? t('common.show.cpmWarnBody')
          : t('common.show.cpmShopBalance').replace('{{balance}}', formatCoins(coinBalance));

  return (
    <div
      className={
        forceShop ? 'cpm-runway cpm-runway--urgent cpm-runway--hold' : 'cpm-runway'
      }
      role="dialog"
      aria-modal="true"
    >
      <div className="cpm-runway__card">
        {phase === 'ok' || showWarnCard ? (
          <button
            type="button"
            className="cpm-runway__dismiss"
            aria-label={t('common.show.cpmDismiss')}
            onClick={() => {
              if (phase === 'warn') {
                setDismissedWarnForShow(show.id);
              }
              onShopOpenChange(false);
            }}
          >
            ×
          </button>
        ) : null}
        {billing ? (
          <p className="cpm-runway__clock">{formatCpmCountdown(clockMs)}</p>
        ) : null}
        <h2 className="cpm-runway__title">{title}</h2>
        <p className="cpm-runway__body">{body}</p>
        {buyError ? <p className="cpm-runway__error">{buyError}</p> : null}
        {packages.length > 0 ? (
          <div className="cpm-runway__packages">
            {packages.slice(0, 6).map((pkg) => (
              <button
                key={pkg.id}
                type="button"
                className="cpm-runway__package"
                disabled={isBuying}
                onClick={() => {
                  void buyCoins({ packageId: pkg.id });
                }}
              >
                <strong>{pkg.label}</strong>
                <span>{formatCoins(pkg.totalCoins)}</span>
                <span className="cpm-runway__package-price">{formatUsdCents(pkg.usdCents)}</span>
              </button>
            ))}
          </div>
        ) : null}
        <div className="cpm-runway__actions">
          <Button
            label={isBuying ? t('common.show.cpmBuying') : t('common.show.cpmAutoReload')}
            disabled={isBuying}
            onClick={() => {
              void handleAutoReload();
            }}
          />
          <p className="cpm-runway__hint">
            {limits?.autoTopUpEnabled
              ? t('common.show.cpmAutoReloadOn').replace(
                  '{{amount}}',
                  formatCoins(limits.autoTopUpAmountCoins),
                )
              : t('common.show.cpmAutoReloadHint')}
          </p>
        </div>
      </div>
    </div>
  );
}
